/* eslint-disable max-lines */
import React from 'react';
import { Column, localizationString } from 'models/response';
import { Row, Cell, SortByFn, Row as IRow } from 'react-table';
import { UserDate } from 'models/user';
import Icon from 'components/Input/Icon';
import styled from 'styled-components/macro';
import { TableColumn } from 'components/Table';
import utils from 'utils';
import { LocalKeys, MatchingType } from 'components/Table/interfaces';
import Checkbox from 'components/Buttons/CheckboxButton';
import StatusIcon from 'components/StatusIcon/StatusIcon';

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface Args {
  DUPLICATED_PROTOCOL: number;
  DOC_STATUS_STOPLIGHT: number;
  NO_SUBJECT: number;
  SAP_POSTED_STATUS: number;
  PRIORITY_ICONS: number;
  t: (label: string) => string;
}

const sortColumnsByPosition = (columnA: Column, columnB: Column) => (columnA.position > columnB.position ? 1 : -1);

const prepareCell = (
  type: 'Number' | 'String' | 'Date' | 'StringDate' | localizationString | 'Checkbox' | 'Boolean',
  timeZone: any,
  name: string,
) => {
  const values: {
    Cell?: ({ cell }: { cell: Cell }) => JSX.Element | null | string;
    hasLocalKeys?: LocalKeys;
    matchingType?: MatchingType;
  } = { hasLocalKeys: undefined, Cell: undefined, matchingType: undefined };
  const dateCell = ({ cell }: { cell: Cell }) => (
    <div title={utils.date.convertDate(cell.value, timeZone ?? 'UTC')}>
      {utils.date.convertDate(cell.value, timeZone ?? 'UTC')}
    </div>
  );
  const checkboxCell = ({ cell }: { cell: Cell }) => {
    return (
      <div style={{ display: 'flex', height: '100%' }}>
        <Checkbox isActive={cell.value} isEditable={false} />
      </div>
    );
  };
  switch (type) {
    case 'Date':
      values.Cell = dateCell;
      values.hasLocalKeys = ['date', undefined];
      break;
    case 'StringDate':
      values.Cell = dateCell;
      values.hasLocalKeys = ['stringDate', undefined];
      break;
    case 'LocalizableString':
    case 'LocalizableStringFe':
      values.Cell = undefined;
      values.hasLocalKeys = [type, 'documentList'];
      break;
    case 'Checkbox':
      values.Cell = checkboxCell;
      break;
    case 'Boolean':
      values.Cell = ({ cell }: { cell: Cell }) => <StatusIcon isSuccess={cell.value === 'true' ? true : false} />;
      values.hasLocalKeys = ['LocalizableStringFe', 'documentList'];
      values.matchingType = 'equal';
      break;
    default:
      values.Cell = ({ cell }: { cell: Cell }) => utils.converter.decodeDocumentProperty(name, cell.value);
      break;
  }
  return values;
};

const prepareColumns = (column: Column, dateFormat?: UserDate, rows?: any[], timeZone?: string): TableColumn => {
  const { name, type, filterType } = column;
  if ((type === 'LocalizableString' || type === 'LocalizableStringFe') && filterType !== 'free') {
    utils.app.notify('warning', `LocalizableString can only be used with free filter for ${column.translate}`);
  }
  const preparedColumn: TableColumn = {
    Header: column.translate,
    width: column.width,
    isVisible: column.position > -1,
    accessor: name,
    id: `${column.idTemplateColumn}`,
    filterType,
    dateFormat,
    filter:
      type === 'Date' || type === 'StringDate'
        ? utils.date.epochFilterFunction(timeZone ?? 'UTC', Boolean(type === 'StringDate'))
        : undefined,
    ...prepareCell(type, timeZone, name),
  };
  if (type === 'StringDate') {
    const colForStringDate: TableColumn = {
      ...preparedColumn,
      sortType: (rowA: any, rowB: any, columnId: string) => {
        const rowDateA = rowA.values[columnId];
        const rowDateB = rowB.values[columnId];
        const pattern = /(\d{2})-(\d{2})-(\d{4})/;

        if (rowDateA) {
          if (rowDateB) {
            return new Date(rowDateA.replace(pattern, '$3-$2-$1')) > new Date(rowDateB.replace(pattern, '$3-$2-$1'))
              ? 1
              : -1;
          } else {
            return 1;
          }
        } else {
          return -1;
        }
      },
    };
    return colForStringDate;
  }
  return preparedColumn;
};

const Span = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const makeStatusColumn = (args: Args): TableColumn => {
  const { DUPLICATED_PROTOCOL, DOC_STATUS_STOPLIGHT, NO_SUBJECT, SAP_POSTED_STATUS, PRIORITY_ICONS, t } = args;
  return {
    Header: t('status'),
    id: 'status',
    filterType: 'none',
    accessor: (row: any) => ({
      duplicatedProtocol: row.duplicatedProtocol,
      duplicationLevel: row.duplicationLevel,
      docStatus: row.docStatus,
      idSubject: row.idSubject,
      registrationProtocol: row.registrationProtocol,
      protocolIn: row.protocolIn,
      leaseStatus: row.leaseStatus,
      assetRecognized: row.assetRecognized,
    }),
    width: 35 * (DUPLICATED_PROTOCOL + DOC_STATUS_STOPLIGHT + NO_SUBJECT + SAP_POSTED_STATUS + PRIORITY_ICONS + 1),
    disableResizing: true,
    Cell: ({ row }: { row: IRow<{}> }) => {
      try {
        const { priority } = row.original as { [key: string]: any };
        const { duplicatedProtocol, duplicationLevel, docStatus, idSubject, registrationProtocol } = row.values.status;

        const title = `${t('duplicated')}: ${duplicatedProtocol}${
          duplicationLevel
            ? `,
           ${t('duplicationLevel')}: ${utils.app.textTranslated(duplicationLevel, t)} `
            : ''
        }`;

        const icons: JSX.Element[] = [];
        let iconIndex = 0;

        // Position 0: DOC_STATUS_STOPLIGHT
        if (DOC_STATUS_STOPLIGHT) {
          if (docStatus > 0) {
            switch (docStatus) {
              case 1:
                icons[iconIndex] = (
                  <Span key="doc-status-1" title={t('supplier not recognized')}>
                    <Icon icon="red-triangle" size={16} />
                  </Span>
                );
                break;
              case 2:
                icons[iconIndex] = (
                  <Span key="doc-status-2" title={t('missing-fields')}>
                    <Icon icon="yellow-square" size={16} />
                  </Span>
                );
                break;
              case 3:
                icons[iconIndex] = (
                  <Span key="doc-status-3" title={t('data completed')}>
                    <Icon icon="green-circle" size={16} />
                  </Span>
                );
                break;
            }
          } else {
            icons[iconIndex] = (
              <Span
                title=""
                key="doc-status-placeholder"
                style={{ width: 30, height: 30, display: 'inline-block', visibility: 'hidden' }}
              />
            );
          }
          iconIndex++;
        }

        // Position 1: PRIORITY_ICONS
        if (PRIORITY_ICONS) {
          if (priority !== undefined) {
            const priorityType = () => {
              switch (priority) {
                case 1:
                  return 'priority-medium';
                case 2:
                  return 'priority-high';
                case 3:
                  return 'critical-priority';
                default:
                  return 'priority-low';
              }
            };

            icons[iconIndex] = (
              <Span key="priority" title={t(`${priorityType()}`)}>
                <Icon icon={priorityType()} size={16} />
              </Span>
            );
          } else {
            icons[iconIndex] = (
              <Span
                title=""
                key="priority-placeholder"
                style={{ width: 30, height: 30, display: 'inline-block', visibility: 'hidden' }}
              />
            );
          }
          iconIndex++;
        }

        // Position 2: NO_SUBJECT
        if (NO_SUBJECT) {
          if (!idSubject && idSubject !== undefined) {
            icons[iconIndex] = (
              <Span key="no-subject" title={t('no provider')}>
                <Icon icon="warning" size={16} />
              </Span>
            );
          } else {
            icons[iconIndex] = (
              <Span
                title=""
                key="no-subject-placeholder"
                style={{ width: 30, height: 30, display: 'inline-block', visibility: 'hidden' }}
              />
            );
          }
          iconIndex++;
        }

        // Position 3: DUPLICATED_PROTOCOL
        if (DUPLICATED_PROTOCOL) {
          if (duplicatedProtocol !== null && duplicatedProtocol !== undefined && duplicatedProtocol !== 0) {
            icons[iconIndex] = (
              <Span key="duplicated" title={title}>
                <Icon icon="duplicated-icon" size={16} />
              </Span>
            );
          } else {
            icons[iconIndex] = (
              <Span
                title=""
                key="duplicated-placeholder"
                style={{ width: 30, height: 30, display: 'inline-block', visibility: 'hidden' }}
              />
            );
          }
          iconIndex++;
        }

        // Position 4: SAP_POSTED_STATUS
        if (SAP_POSTED_STATUS) {
          const isSapPosted = Boolean(registrationProtocol);
          if (isSapPosted) {
            icons[iconIndex] = (
              <Span key="sap-posted" title={t('posted_to_sap')}>
                <Icon icon="exportToSapColored" size={16} />
              </Span>
            );
          } else {
            icons[iconIndex] = (
              <Span
                title=""
                key="sap-posted-placeholder"
                style={{ width: 30, height: 30, display: 'inline-block', visibility: 'hidden' }}
              />
            );
          }
          iconIndex++;
        }

        return <IconsContainer>{icons}</IconsContainer>;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
  };
};

const matchesFilter = (
  row: any,
  columnId: string,
  filterValue: string,
  t?: (label: string) => string,
  hasLocalKeys?: LocalKeys,
  header?: string,
  matchingType?: MatchingType,
  voidValue = '_',
) => {
  const [localKeys, prefixName] = hasLocalKeys ?? [undefined, undefined];

  const rowValue = row?.values[columnId] != null ? row?.values[columnId] : matchingType === 'equal' ? 'false' : '';

  const translatedValue = localKeys
    ? String(
        localKeys === 'LocalizableStringFe'
          ? utils.app.setLocalizableString(rowValue, header, prefixName, t)
          : utils.app.textTranslated(rowValue, t),
      ).toLowerCase()
    : String(rowValue).toLowerCase();

  const filterValueLower = String(filterValue).toLowerCase();

  if (filterValue === voidValue) {
    return (
      (translatedValue ?? '') ===
      (matchingType === 'equal' ? String(localKeys ? utils.app.textTranslated('false', t) : 'false').toLowerCase() : '')
    );
  }
  switch (matchingType) {
    case 'equal':
      return translatedValue.toLowerCase() === filterValueLower.toLowerCase();
    case 'includes':
    default:
      return translatedValue.includes(filterValueLower);
  }
};

// standard separator for multiValueSearch
type separatorType = ',';
/**
 * function used to filter multi value text by a chosen separator
 * @param {separatorType} separator - separator used to split date
 * @param {string} voidValue - value to be used when the cell doesn't have a value
 * @param {function} t - translation function
 * @param {LocalKeys} hasLocalKeys - used to find keys in o_local
 * @param {string} header - used to find keys in o_local
 * @param {MatchingType} matchingType - type of matching to apply ('includes' or 'equal')
 * @param {unknown[]} rows all table rows to be filtered
 * @param {string[]} ids expected in the default lucy DD-MM-YYYY format
 * @param {string | string[]} filterValue expected in the default lucy DD-MM-YYYY format
 * @return {unknown[]} filtered rows
 */
const multiValueFilterFunction = (
  separator: separatorType = ',',
  voidValue = '_',
  t?: (label: string) => string,
  hasLocalKeys?: LocalKeys,
  header?: string,
  matchingType?: MatchingType, // Add matching type parameter
) => {
  return (rows: unknown[], columnIds: string[], filterValue: string | string[]): unknown[] => {
    if (!filterValue || columnIds.length !== 1) return rows;
    const filterValues = new Set(
      Array.isArray(filterValue) ? filterValue : filterValue.split(separator).filter((v) => v),
    );
    const uniqueRows = new Set();
    if (filterValues.size === 0) return rows;
    rows.forEach((row) => {
      for (const value of filterValues) {
        if (matchesFilter(row, columnIds[0], value, t, hasLocalKeys, header, matchingType, voidValue)) {
          uniqueRows.add(row);
          break;
        }
      }
    });
    return Array.from(uniqueRows);
  };
};

const selectValueFilterFunction =
  (t?: (label: string) => string) =>
  (rows: any[], columnIds: string[], filterValue: string): any[] => {
    if (filterValue === null || filterValue === undefined || filterValue === '') return rows;

    const out: any[] = [];

    for (const row of rows) {
      let matched = false;

      // check main row values
      for (const colId of columnIds) {
        if (matchesFilter(row, colId, filterValue, t, undefined, undefined, 'equal')) {
          matched = true;
          break;
        }
      }

      // always check subRows as well
      if (!matched && row.subRows && row.subRows.length) {
        for (const subRow of row.subRows) {
          for (const colId of columnIds) {
            if (matchesFilter(subRow, colId, filterValue, t, undefined, undefined, 'equal')) {
              matched = true;
              break;
            }
          }
          if (matched) break;
        }
      }

      if (matched) out.push(row);
    }

    return out;
  };
/**
 * this function is used to filter the content of a cell that contains a subRows
 * @param {any[]} rows
 * @param {string[]} columnIds
 * @param {any} filterValue
 * @return {any[]}
 */
const contentFilter = (rows: any[], columnIds: string[], filterValue: any): any[] => {
  const colId = columnIds[0]; // id of the column
  const setRows = new Set();
  const re = new RegExp(filterValue, 'i');
  rows.forEach((row: any) => {
    const editedRow = { ...row };
    editedRow.subRows = [];
    // we need to add into editedRow the subRows that match the
    // filter and then adding it into setRows
    if (row.subRows.length > 0) {
      row.subRows.forEach((subRow: any) => {
        if (re.test(subRow.values[colId])) {
          if (!setRows.has(editedRow)) {
            setRows.add(editedRow);
          }
          editedRow.subRows.push(subRow);
        }
      });
    } else {
      re.test(row.values[colId]) && setRows.add(row);
    }
    return;
  });
  return Array.from(setRows);
};

type Option = {
  label: string;
  value: string;
};
/**
 * this function build the options for the select input analyzing the values of the column and the values of subrows
 * @param { any[] } rows
 * @param { string | number } accessor
 * @param { string } associatedOptions
 * @param { function } t
 * @param { LocalKeys} hasLocalKeys
 * @param { string } header
 * @param { MatchingType } matchingType
 * @return { Option[] }
 */
const buildOptionsByRows = (
  rows: any[],
  accessor: string | number,
  associatedOptions?: Map<string, string>,
  t?: (label: string) => string,
  hasLocalKeys?: LocalKeys,
  header?: string,
  matchingType?: MatchingType,
): Option[] => {
  const options = new Set<string>();
  const [localKeys, prefixName] = hasLocalKeys ?? [undefined, undefined];
  const translateFunction =
    localKeys === 'LocalizableStringFe'
      ? (val: any) => utils.app.setLocalizableString(val, header, prefixName, t)
      : (val: any) => utils.app.textTranslated(val, t);

  rows.forEach((row) => {
    if (row.values[accessor] !== '-') options.add(String(translateFunction(row.values[accessor])));
    // if there are subrows id should be equal to the accessor
    if (row?.originalSubRows) {
      row?.originalSubRows?.forEach((subRow: any) => {
        if (subRow[accessor] === '-') return;
        options.add(String(translateFunction(subRow[accessor])));
      });
    }
  });
  const matchedOptions =
    matchingType === 'equal'
      ? Array.from(options).filter((option) => option !== 'undefined' && option !== 'null' && option !== '')
      : Array.from(options).filter((option) => option !== 'undefined' && option !== 'null');

  return matchedOptions.sort().map((option) => {
    return {
      value: option || '_',
      label: associatedOptions?.get?.(option) ?? option ? option : 'Empty',
    };
  });
};
/**
 * this function is the same of the previous one but it returns the options counting
 * the number of occurences of each value and adding the count to the label
 * @param { any[] } rows
 * @param { string | number } accessor
 * @return { Option[] }
 */
const buildOptionsByRowsWithCount = (rows: Row[], accessor: string | number): Option[] => {
  const options = new Map<string, number>();
  rows.forEach((row) => {
    if (row.values[accessor] == null) return;
    if (row.values[accessor] !== '-') {
      const value = String(row.values[accessor]);
      if (options.has(value) && options.get(value) !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        options.set(value, options.get(value)! + 1);
      } else {
        options.set(value, 1);
      }
    }
    // i need to add subrows too
    if (row.subRows) {
      row.subRows.forEach((subRow: any) => {
        if (row.values[accessor] == null && row.values[accessor] === '-') return;
        const value = String(subRow.values[accessor]);
        if (options.has(value) && options.get(value) !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          options.set(value, options.get(value)! + 1);
        } else {
          options.set(value, 1);
        }
      });
    }
  });
  return Array.from(options)
    .map((option) => {
      return {
        value: option[0] || '_',
        label: option[0] ? `${option[0]} (${option[1]})` : `Empty (${option[1]})`,
      };
    })
    .sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
};

const booleanSort: SortByFn<{}> = (rowA, rowB, columnId, desc = false) => {
  const valueA: boolean = rowA.values[columnId];
  const valueB: boolean = rowB.values[columnId];

  if (valueA === valueB) {
    return 0;
  }
  if (desc) return valueB ? -1 : 1;
  return valueA ? 1 : -1;
};

const dateSort: SortByFn<{}> = (rowA, rowB, columnId, desc = false) => {
  const valueA: string = rowA.values[columnId];
  const valueB: string = rowB.values[columnId];

  const formatDateToISO = (dateString: string) => {
    const [day, month, yearAndTime] = dateString.split('-');
    const [year, time] = yearAndTime.split(', ');
    return `${year}-${month}-${day}T${time}`;
  };

  const d1 = new Date(formatDateToISO(valueA));
  const d2 = new Date(formatDateToISO(valueB));

  if (d1.getTime() === d2.getTime()) {
    return 0;
  }
  if (desc) return d1.getTime() > d2.getTime() ? -1 : 1;
  return d1.getTime() < d2.getTime() ? 1 : -1;
};

export default {
  sortColumnsByPosition,
  prepareColumns,
  makeStatusColumn,
  multiValueFilterFunction,
  contentFilter,
  buildOptionsByRows,
  buildOptionsByRowsWithCount,
  booleanSort,
  dateSort,
  selectValueFilterFunction,
};
